import { apiRequest } from './axiosConfig';
import { handleApiError } from '../../utils/errorHandling';

// 広告自動化マンのRAG的な情報源になるヘルプ記事（FIXES.md F18-b）。
// 利用者には直接見せない（AIが回答に引用する内部ナレッジ）。
// バックエンドのAdmanGuardが応答からAPIパス・環境変数名・SQL・AIベンダー名/モデル名を
// 検出すると定型文に差し替えるため、これらを含む記事を保存すると warnings が返る
// （保存自体はブロックされず201/200で返る。弾かずに保存して警告だけ返す設計）。
//
// バリデーション（バックエンド実測）: title required max255 / body required max100000 /
// is_published boolean / sort_order integer 0〜100000。
export interface HelpArticle {
  id: number;
  title: string;
  body: string;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface HelpArticleListResponse {
  data: HelpArticle[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface HelpArticleCreateRequest {
  title: string;
  body: string;
  is_published: boolean;
  sort_order?: number;
}

export interface HelpArticleUpdateRequest {
  title?: string;
  body?: string;
  is_published?: boolean;
  sort_order?: number;
}

// 保存は成功するが、AdmanGuardに引っかかりうる表記が本文に含まれる場合に警告が付く。
// 保存自体はブロックしない（管理者の判断を尊重する）。
export interface HelpArticleMutationResult {
  article: HelpArticle;
  warnings: string[];
}

// Admin: fetch all help articles (published and unpublished)
export const adminFetchHelpArticles = async (page: number = 1): Promise<HelpArticleListResponse> => {
  try {
    const response = await apiRequest.get<{ data: HelpArticleListResponse }>(`/admin/help-articles?page=${page}`);
    return (response as any).data as HelpArticleListResponse;
  } catch (error) {
    throw handleApiError(error, 'ヘルプ記事一覧の取得に失敗しました');
  }
};

// Admin: fetch a single help article.
// GET /{id} も warnings を返す（あとから禁止語を足したケースに気づけるよう、
// 既存記事を開いたときにも警告が出る仕様）。編集フォームを開く時に使う。
export const adminFetchHelpArticle = async (id: number): Promise<HelpArticleMutationResult> => {
  try {
    const response = await apiRequest.get<{ data: HelpArticle; warnings?: string[] }>(
      `/admin/help-articles/${id}`
    );
    return {
      article: (response as any).data as HelpArticle,
      warnings: (response as any).warnings ?? [],
    };
  } catch (error) {
    throw handleApiError(error, 'ヘルプ記事の取得に失敗しました');
  }
};

// Admin: create help article
export const adminCreateHelpArticle = async (data: HelpArticleCreateRequest): Promise<HelpArticleMutationResult> => {
  try {
    const response = await apiRequest.post<{ data: HelpArticle; warnings?: string[] }>('/admin/help-articles', data);
    return {
      article: (response as any).data as HelpArticle,
      warnings: (response as any).warnings ?? [],
    };
  } catch (error) {
    throw handleApiError(error, 'ヘルプ記事の作成に失敗しました');
  }
};

// Admin: update help article
export const adminUpdateHelpArticle = async (
  id: number,
  data: HelpArticleUpdateRequest
): Promise<HelpArticleMutationResult> => {
  try {
    const response = await apiRequest.put<{ data: HelpArticle; warnings?: string[] }>(
      `/admin/help-articles/${id}`,
      data
    );
    return {
      article: (response as any).data as HelpArticle,
      warnings: (response as any).warnings ?? [],
    };
  } catch (error) {
    throw handleApiError(error, 'ヘルプ記事の更新に失敗しました');
  }
};

// Admin: delete help article
export const adminDeleteHelpArticle = async (id: number): Promise<void> => {
  try {
    await apiRequest.delete(`/admin/help-articles/${id}`);
  } catch (error) {
    throw handleApiError(error, 'ヘルプ記事の削除に失敗しました');
  }
};
